"use client";

import Link from "next/link";
import type { ComponentProps } from "react";
import {
  SignedIn,
  SignedOut,
  UserButton,
  ClerkLoading,
  ClerkLoaded,
} from "@clerk/nextjs";
import { usePathname, useRouter } from "next/navigation";

import { DesktopNav } from "./DesktopNav";
import { Logo } from "@/components/typography/Logo";
import { SearchForm } from "./SearchForm";
import { Button } from "@/components/ui/button";
import { ArrowRightThin } from "@/components/icons";
import { secondaryLinks, SOCIAL_LINKS } from "@/lib/nav-data";
import { cn } from "@/lib/utils";
import { useAuthModal } from "@/components/auth/AuthModalContext";
import { ProfileSettingsPage } from "@/components/auth/ProfileSettingsPage";

const Header = ({ children, ...rest }: ComponentProps<"header">) => {
  const pathname = usePathname();
  const router = useRouter();
  const { setOpen: openAuthModal } = useAuthModal();

  const isLibrary = pathname === "/library";

  return (
    <header
      className={cn(
        "hidden lg:block sticky top-0 z-50",
        isLibrary && "absolute w-full bg-transparent",
        rest.className
      )}
    >
      {/* Row 1: Top bar */}
      <div className={cn("bg-sand-50", isLibrary && "bg-transparent")}>
        <div className="max-w-7xl mx-auto px-8 h-12 flex items-center justify-between">
          {/* Left: secondary links */}
          <nav className="flex items-center gap-8">
            {secondaryLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-label text-black hover:opacity-70 transition-opacity"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right: social icons */}
          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map(({ href, label, Icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="hover:opacity-70 transition-opacity"
              >
                <Icon className="size-7 text-black" />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Main nav */}
      <div
        className={cn("bg-sand-10", {
          "bg-sun-100": pathname === "/partner/b2b",
          "bg-transparent": isLibrary,
        })}
      >
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between py-3">
          {/* Left: Logo */}
          <Link href="/" aria-label="Dialetics" className="flex-shrink-0">
            <Logo />
          </Link>

          {/* Center: Primary nav */}
          <DesktopNav />

          {/* Right: Search + Auth */}
          <div className="flex items-center gap-2 min-w-0">
            <SearchForm glassmorphism className="shrink-[3] min-w-[160px] w-[160px] min-[1150px]:w-[180px] xl:w-[247px]" />

            <ClerkLoading>
              <div className="h-[46px] w-[180px] bg-sand-100 rounded-full animate-pulse flex-shrink-0" />
            </ClerkLoading>
            <ClerkLoaded>
              <SignedIn>
                {pathname !== "/dashboard" && (
                  <Button
                    variant="black"
                    size="sm"
                    asChild
                    className="flex-shrink-0"
                  >
                    <Link href="/dashboard">Mein Dashboard</Link>
                  </Button>
                )}
                <div className="sr-only">
                <UserButton>
                  <UserButton.MenuItems>
                    <UserButton.Action label="signOut" />
                  </UserButton.MenuItems>
                  <UserButton.UserProfilePage label="Dialetics" url="settings" labelIcon={<span>✏️</span>}>
                    <ProfileSettingsPage />
                  </UserButton.UserProfilePage>
                  <UserButton.UserProfilePage label="account" />
                  <UserButton.UserProfilePage label="security" />
                  <UserButton.UserProfileLink
                    label="Mitgliedschaft"
                    url="/user/membership"
                    labelIcon={
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.3333 2H2.66667C1.93029 2 1.33333 2.59695 1.33333 3.33333V12.6667C1.33333 13.403 1.93029 14 2.66667 14H13.3333C14.0697 14 14.6667 13.403 14.6667 12.6667V3.33333C14.6667 2.59695 14.0697 2 13.3333 2Z" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1.33333 6H14.6667" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    }
                  />
                  <UserButton.UserProfileLink
                    label="Abmelden"
                    url="/sign-out"
                    labelIcon={
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6M10.6667 11.3333L14 8M14 8L10.6667 4.66667M14 8H6" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    }
                  />
                </UserButton>
                </div>
              </SignedIn>
              <SignedOut>
                <Button
                  variant="black"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => window.innerWidth < 1280 ? router.push("/login") : openAuthModal(true)}
                  data-cta-location="header"
                  data-cta-label="Jetzt anmelden"
                >
                  Anmelden
                  <ArrowRightThin className="ml-2 size-5" />
                </Button>
              </SignedOut>
            </ClerkLoaded>
          </div>
        </div>
      </div>
      {children}
    </header>
  );
};

export { Header };
